import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  type WPTableColumn,
  type WPTableRow,
} from '@repo/supergraph/structuredContent/hydration/WPTable/types';
import { type StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { AccessibleHiddenText } from '@repo/ui/AccessibleHiddenText';
import { StructuredContentRenderer } from '@repo/ui/StructuredContentRenderer';

export type WPTableProps = StructuredContentDocument<{
  headers: WPTableColumn[];
  rows: WPTableRow[];
  tableBolding: '' | 'NONE' | 'FIRST_ROW' | 'FIRST_COLUMN' | 'FIRST_ROW_COLUMN';
  isStriped: boolean;
  rowHover: boolean;
}>;

const getTableBoldStyling = (tableBolding: string, rowIndex: number, columnIndex: number) => {
  let isBold = false;

  if (tableBolding === 'FIRST_ROW') {
    isBold = rowIndex === 0;
  } else if (tableBolding === 'FIRST_COLUMN') {
    isBold = columnIndex === 0;
  } else if (tableBolding === 'FIRST_ROW_COLUMN') {
    isBold = rowIndex === 0 || columnIndex === 0;
  }
  return isBold;
};

export const WPTable = ({ props }: WPTableProps) => {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        borderTop: '2px solid',
        borderTopColor: 'primary.main',
        overflowX: 'auto',
      }}
    >
      <Table>
        {props.headers && (
          <TableHead>
            <TableRow>
              {props?.headers?.map((header, i) => {
                return (
                  <TableCell
                    key={`sc-table-header-${i}`}
                    data-testid={`sc-table-header-${i}`}
                    variant="head"
                    colSpan={header.colspan}
                    sx={{
                      paddingX: { md: 2, xs: 3 },
                      paddingY: { md: 3, xs: 2 },
                      minWidth: '140px',
                      backgroundColor: 'grey.100',
                      borderBottom: '1px solid',
                      borderBottomColor: 'grey.200',
                      verticalAlign: 'top',
                      '&:first-of-type': {
                        paddingLeft: 3,
                      },
                      '&:not(:first-of-type)': {
                        borderLeft: '1px solid',
                        borderLeftColor: 'grey.200',
                      },
                      '& *': {
                        ...(!props.tableBolding && { fontWeight: 700 }),
                      },
                    }}
                  >
                    {header.content ? (
                      <StructuredContentRenderer doc={header.content} />
                    ) : (
                      <AccessibleHiddenText>Empty Table Header</AccessibleHiddenText>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {props?.rows?.map((row: WPTableRow, i) => (
            <TableRow
              key={`sc-table-row-${i}`}
              sx={{
                '&:nth-of-type(odd)': {
                  backgroundColor: 'common.white',
                },
                '&:nth-of-type(even)': {
                  backgroundColor: props.isStriped ? 'grey.100' : 'common.white',
                },
                '&:hover td': {
                  backgroundColor: props.rowHover ? 'secondary.light' : 'inherit',
                },
              }}
            >
              {row.columns.map((col: WPTableColumn, j) => {
                return (
                  <TableCell
                    colSpan={col.colspan}
                    key={`sc-table-col-${col.id}`}
                    sx={{
                      '&:first-of-type': {
                        paddingLeft: 3,
                      },
                      '&:not(:first-of-type)': {
                        borderLeft: '1px solid',
                        borderLeftColor: 'grey.200',
                      },
                      paddingX: { md: 3, xs: 3 },
                      paddingY: { md: 3, xs: 2 },
                      minWidth: '140px',
                      '& *': {
                        ...(getTableBoldStyling(props.tableBolding, i, j) && { fontWeight: 700 }),
                      },
                    }}
                  >
                    {col.content && <StructuredContentRenderer doc={col.content} />}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
